import verityLogger from "./logger";
import apiClient from "./api-client";
import { createRoot } from "react-dom/client";
import { CoreSpinner } from "@verity/components/core";

const checkIsIOSVerityMobileApp = () =>
	/iPhone|iPad|iPod/.test(navigator.userAgent) && navigator.userAgent.includes("VerityLockdownMobile");

function isCrossOriginWindowObject(win: Window) {
	let isCrossOrigin = false;
	if (win !== window.self) {
		try {
			win.location.hostname;
		} catch {
			isCrossOrigin = true;
		}
	}
	return isCrossOrigin;
}

/**
 * Open a new tab with the specified URL and initialize listeners to handle request
 * for an authentication code.
 *
 * @param url The URL to open in a new tab.
 */
export function openAuthenticatedTab(url: string | URL) {
	const urlWithParam = new URL(url, location.origin);
	const isCrossOriginWindow = window.top && isCrossOriginWindowObject(window.top);
	const isIOSVerityMobileApp = checkIsIOSVerityMobileApp();

	// If the window is cross-origin, notify the new tab via search param
	if (isCrossOriginWindow) {
		urlWithParam.searchParams.set("authenticateFrom", isIOSVerityMobileApp ? "top" : "opener");
	}

	// Open a window in a new tab and log the event. The openedWindow value will be null if isIOSVerityMobileApp is true.
	const openedWindow = window.open(urlWithParam, "_blank");
	if (isCrossOriginWindow) {
		verityLogger.authentication.authenticatedPage.opened("tab", String(url));
	}

	// Attach a message listener the new window will use to request an auth code
	if (openedWindow && isCrossOriginWindow) {
		const onMessage = async (event: MessageEvent) => {
			try {
				if (event.data?.type === "requestAuthCode") {
					verityLogger.authentication.authenticatedPage.authenticateMessageReceived(
						{ origin: event.origin },
						event.data,
					);
				}
				if (
					event.source === openedWindow &&
					event.origin === location.origin &&
					event.data?.type === "requestAuthCode"
				) {
					// Remove message listener
					window.removeEventListener("message", onMessage);

					// Log auth code request
					const accountId = await apiClient.getAccountId();
					if (accountId) {
						verityLogger.authentication.authCode.requested("new-tab", accountId);
					}

					// Get auth code
					const payload = await apiClient.authentication.getAccessControlAuthCode();
					if (!payload || !payload.accountId) {
						return;
					}

					verityLogger.authentication.authCode.created(payload.accountId);

					// Send auth code to new window
					openedWindow.postMessage(
						{
							type: "authCode",
							payload,
						},
						location.origin,
					);
					verityLogger.authentication.authenticatedPage.authCodeSent(location.origin, payload.accountId);
				}
			} catch (e) {
				verityLogger.error(e);
			}
		};
		window.addEventListener("message", onMessage);
		setTimeout(() => window.removeEventListener("message", onMessage), 30000);
	}
	return openedWindow;
}

/**
 * Middleware that handles the case where the portal is opened from an authenticated tab
 *
 * Requests an auth code from the parent window and initializes required apiClient
 * state for the portal to use
 *
 */
export async function handleAuthenticatedTabOpen() {
	// Show a spinner while handling
	let containerRef: HTMLDivElement | null = null;
	try {
		const container = document.createElement("div");
		containerRef = container;

		document.body.prepend(container);
		const root = createRoot(container);
		root.render(
			<div
				style={{
					width: "100",
					height: "100vh",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					zIndex: "auto",
				}}
			>
				<CoreSpinner size={100} />
			</div>,
		);
	} catch (e) {
		verityLogger.error(e);
	}

	try {
		// Determine if the portal is opened from an authenticated tab
		const currUrl = new URL(window.location.href);
		const authenticateFrom = currUrl.searchParams.get("authenticateFrom");
		let authenticateWindow: Window | undefined;

		// If no authenticateFrom param, return
		if (!authenticateFrom) {
			return;
		}

		// Determine the window that will provide the auth code
		verityLogger.authentication.authenticatedPage.detected(authenticateFrom);
		currUrl.searchParams.delete("authenticateFrom");
		window.history.replaceState(window.history.state, "", currUrl);
		if (authenticateFrom === "opener") {
			authenticateWindow = window.opener;
		} else if (authenticateFrom === "parent") {
			authenticateWindow = window.parent;
		} else if (authenticateFrom === "top") {
			authenticateWindow = window.top ?? undefined;
		}

		// If no window is found, return
		if (!authenticateWindow) {
			verityLogger.authentication.authenticatedPage.authenticationSkipped({
				type: "no-source",
				sourceType: authenticateFrom,
			});
			return;
		}

		// Otherwise, request an auth code from the authenticating window
		const authCodeInfo = await new Promise<{ accountId: string; authCode: string } | undefined>((resolve) => {
			const onMessage = (event: MessageEvent) => {
				if (event.data?.type === "authCode") {
					verityLogger.authentication.authenticatedPage.authCodeReceived(
						{ origin: event.origin },
						{
							type: event.data?.type,
							payload: {
								accountId: event.data.payload?.accountId,
								authCode: typeof event.data.payload.authCode,
							},
						},
					);
				}
				if (
					event.source === authenticateWindow &&
					event.data?.type === "authCode" &&
					typeof event.data.payload?.accountId === "string" &&
					typeof event.data.payload.authCode === "string"
				) {
					const { accountId, authCode } = event.data.payload;
					window.removeEventListener("message", onMessage);
					clearTimeout(timeout);
					resolve({ accountId, authCode });
				}
			};
			const timeout = setTimeout(() => {
				verityLogger.authentication.authenticatedPage.authenticationSkipped({ type: "timed-out" });
				window.removeEventListener("message", onMessage);
				console.warn("no auth code received from request");
				resolve(undefined);
			}, 10000);

			window.addEventListener("message", onMessage);
			authenticateWindow?.postMessage({ type: "requestAuthCode" }, "*");
			verityLogger.authentication.authenticatedPage.authenticateMessageSent({ type: "requestAuthCode" });
		});

		// If the auth code is not received, return
		if (!authCodeInfo) {
			return;
		}

		// Otherwise, exchange the auth code for a refresh token
		const { refreshToken, accountId, currentUserId } = await apiClient.authentication.getRefreshTokenFromAuthCode(
			authCodeInfo.accountId,
			authCodeInfo.authCode,
		);
		verityLogger.authentication.authenticatedPage.authCodeExchanged(accountId, currentUserId);

		// Update apiClient state
		apiClient.setRefreshToken(refreshToken);
		apiClient.setAccountId(accountId);

		/**
		 * Cannot safely use emitter to signal token updates due to multiple implementations
		 *
		 * Instead, ping the localStorage every 50ms and handle update or lack thereof
		 *
		 */
		const existingAccessToken = await apiClient.getAccessToken();
		const tokenUpdated = new Promise<void>((resolve) => {
			const timeout = setTimeout(() => {
				clearInterval(interval);
				clearTimeout(timeout);
				resolve();
			}, 1000);

			const interval = setInterval(async () => {
				const newAccessToken = await apiClient.getAccessToken();
				// Verify that the new access token exists and is not the same as the existing one
				if (newAccessToken && existingAccessToken !== newAccessToken) {
					clearInterval(interval);
					clearTimeout(timeout);
					resolve();
				}
			}, 50);

			void apiClient.updateTokens();
		});

		// Wait for the access token to be updated
		await tokenUpdated;
		verityLogger.authentication.authenticatedPage.authenticated(accountId, currentUserId);
	} catch (e) {
		verityLogger.error(e);
		throw e;
	} finally {
		if (containerRef) {
			document.body.removeChild(containerRef);
		}
	}
}
